import { Alerts } from 'constants/alerts';
import { DeleteStages } from 'constants/deleteStages';

import { useApiContext } from 'context/apiContext';
import { usePermissions } from 'hooks/usePermissions';
import { useRecordModal } from 'hooks/useRecordModal';
import { useStagedDelete } from 'hooks/useStagedDelete';
import { MerchantsArchiveDialog } from 'pages/Merchants/components/MerchantsArchiveDialog/MerchantsArchiveDialog';
import { MerchantsUnarchiveDialog } from 'pages/Merchants/components/MerchantsUnarchiveDialog/MerchantsUnarchiveDialog';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { queryClient } from 'providers/queryClient';
import { Alert } from 'shared/mui/Alert/Alert';
import { CancelButton, CreateButton } from 'shared/mui/Buttons';
import { DeleteStageDialog } from 'shared/mui/Dialogs/DeleteStageDialog/DeleteStageDialog';
import { DeleteStageDialogDenyReason } from 'shared/mui/Dialogs/DeleteStageDialog/DeleteStageDialogDenyReason';
import { Typography } from 'shared/mui/Typography';
import { TypographyBold } from 'shared/mui/Typography/TypographyBold';
import { CompanyShowDialog } from 'shared/widgets/CompanyShowDialog/CompanyShowDialog';
import { Company } from 'types/embedded/company';
import { Merchant } from 'types/embedded/merchant';
import { Permission } from 'types/embedded/permission';

import { ListPageStyled } from './MerchantsList.styled';
import { MerchantsListDatagrid } from './MerchantsListDatagrid/MerchantsListDatagrid';
import { MerchantsListFilter } from './MerchantsListFilter/MerchantsListFilter';

export const MerchantsList: React.FC = () => {
  const translate = useTypedTranslate();

  const { hasResourcePermissions } = usePermissions();
  const {
    record: recordShow,
    handleOpenRecordModal: handleOpenRecordModalShow,
    handleCloseRecordModal: handleCloseRecordModalShow,
    openRecordModal: openRecordModalShow,
  } = useRecordModal<Partial<Company>>();

  const {
    record: recordDelete,
    handleOpenRecordModal: handleOpenRecordModalDelete,
    openRecordModal: openRecordModalDelete,
    handleCloseRecordModal: handleCloseRecordModalDelete,
  } = useRecordModal<Merchant>();

  const {
    record: recordUnarchive,
    handleOpenRecordModal: handleOpenRecordModalUnarchive,
    openRecordModal: openRecordModalUnarchive,
    handleCloseRecordModal: handleCloseRecordModalUnarchive,
  } = useRecordModal<Merchant>();
  const {
    record: recordArchive,
    handleOpenRecordModal: handleOpenRecordModalArchive,
    openRecordModal: openRecordModalArchive,
    handleCloseRecordModal: handleCloseRecordModalArchive,
  } = useRecordModal<Merchant>();

  const { deleteOneRecord, denyDeleteReason, stage, setStage } = useStagedDelete({
    record: recordDelete,
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['merchants'] });
      handleCloseRecordModalDelete();
      setStage(DeleteStages.Initial);
    },
  });

  const handleCloseDeleteDialog = () => {
    setStage(DeleteStages.Initial);
    handleCloseRecordModalDelete();
  };

  const { user } = useApiContext();

  return (
    <ListPageStyled
      actions={[
        hasResourcePermissions('merchants', [Permission.Full, Permission.Create]) && (
          <CreateButton key="create-merchants" />
        ),
      ]}
      empty={false}
      filterDefaultValues={{ archive: false }}
      headerTitle={{
        titleText: translate('app.merchants.merchants'),
        tooltipText: translate('app.merchants.kuberaClientAccount'),
      }}
      sort={{ field: 'id', order: 'DESC' }}
    >
      <MerchantsListFilter />
      {user.alerts.includes(Alerts.EmptyMerchantAccess) ? (
        <Alert severity="warning">
          {translate('app.merchants.noMerchantsAvailableForUser', {
            firstName: user.firstName,
            lastName: user.lastName,
          })}
          .
        </Alert>
      ) : (
        <MerchantsListDatagrid
          handleOpenRecordModalArchive={handleOpenRecordModalArchive}
          handleOpenRecordModalDelete={handleOpenRecordModalDelete}
          handleOpenRecordModalShow={handleOpenRecordModalShow}
          handleOpenRecordModalUnarchive={handleOpenRecordModalUnarchive}
        />
      )}
      {openRecordModalDelete && (
        <DeleteStageDialog
          deleteOneRecord={deleteOneRecord}
          details={
            <Typography>
              {translate('app.merchants.merchantWillBeDeletedPermanently1')}{' '}
              <TypographyBold fontSize={16} text={recordDelete?.name || ''} />{' '}
              {translate('app.merchants.merchantWillBeDeletedPermanently2')}
            </Typography>
          }
          onClose={handleCloseRecordModalDelete}
          open={openRecordModalDelete}
          stage={stage}
          title={translate('app.merchants.merchantDeletion')}
        >
          <DeleteStageDialogDenyReason reason={denyDeleteReason}>
            <CancelButton
              key="history-back-button"
              label={translate('app.merchants.backToMerchantsList')}
              onClick={handleCloseDeleteDialog}
              sx={{ marginLeft: 'auto' }}
              variant="outlined"
            />
          </DeleteStageDialogDenyReason>
        </DeleteStageDialog>
      )}
      {openRecordModalUnarchive && recordUnarchive && (
        <MerchantsUnarchiveDialog
          onClose={handleCloseRecordModalUnarchive}
          open={openRecordModalUnarchive}
          record={recordUnarchive}
        />
      )}
      {openRecordModalArchive && recordArchive && (
        <MerchantsArchiveDialog
          onClose={handleCloseRecordModalArchive}
          open={openRecordModalArchive}
          record={recordArchive}
        >
          <CancelButton
            label={translate('app.merchants.backToMerchantsList')}
            onClick={handleCloseRecordModalArchive}
            sx={{ marginLeft: 'auto' }}
            variant="outlined"
          />
        </MerchantsArchiveDialog>
      )}
      <CompanyShowDialog
        company={recordShow}
        onClose={handleCloseRecordModalShow}
        open={openRecordModalShow}
      />
    </ListPageStyled>
  );
};
